import { ModuleLayout, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'
import { getInvoiceSummaryTotals } from '@features/invoices/utils/getInvoiceSummaryTotals'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../enums/modalId'
import { useForm, useWatch } from '../../../../hooks'
import { demoInvoiceFormSchema } from '../../constants/demoInvoiceFormSchema'
import { DEMO_INVOICE_NUMBER, DEMO_INVOICE_NUMBER_PREFIX } from '../../constants/demoInvoiceNumber'
import { DemoInvoiceSendChannel } from '../../enums/demoInvoiceSendChannel'
import { DemoInvoiceView } from '../../enums/demoInvoiceView'
import { useDemoInvoicePreview } from '../../hooks/useDemoInvoicePreview'
import { DemoInvoiceSentState } from '../../types/demoInvoiceSentState'
import { getDemoInvoiceFormDefaultValues } from '../../utils/getDemoInvoiceFormDefaultValues'
import { DemoInvoiceCompletedModal } from '../DemoInvoiceCompletedModal'
import { DemoInvoiceDownloadButton } from '../DemoInvoiceDownloadButton'
import { DemoInvoiceFinalizeMenu } from '../DemoInvoiceFinalizeMenu'
import { DemoInvoiceForm } from '../DemoInvoiceForm'
import { DemoInvoiceLayout } from '../DemoInvoiceLayout'
import { DemoInvoicePreview } from '../DemoInvoicePreview'
import { DemoInvoiceSendAsEInvoiceModal } from '../DemoInvoiceSendAsEInvoiceModal'
import { DemoInvoiceSendAsEmailModal } from '../DemoInvoiceSendAsEmailModal'
import { DemoInvoiceSentView } from '../DemoInvoiceSentView'
import { DemoInvoiceViewToggle } from '../DemoInvoiceViewToggle'
import * as Styled from './styles'

interface DemoInvoiceEditorProps {
  backButtonUrl: string
  onClose: () => void
  onReset: () => void
}

export const DemoInvoiceEditor = ({ backButtonUrl, onClose, onReset }: DemoInvoiceEditorProps): ReactElement => {
  const { t, i18n } = useTranslation()
  const { organization } = useUserOrganization()
  const [view, setView] = useState(DemoInvoiceView.Editor)
  const [sentState, setSentState] = useState<DemoInvoiceSentState>()
  const [discountMode, setDiscountMode] = useState(InvoiceDiscountMode.None)
  const { open: openCompletedModal } = useModal(ModalId.DemoInvoiceCompletedModal)

  const defaultValues = useMemo(
    () => getDemoInvoiceFormDefaultValues({ language: i18n.language, organization, t }),
    [i18n.language, organization, t],
  )

  const { control, Form, getValues } = useForm({ defaultValues, validationSchema: demoInvoiceFormSchema })
  const { htmlFiles, isLoading } = useDemoInvoicePreview(control, discountMode)
  const receiver = useWatch({ control, name: 'receiver' })

  const showSentInvoice = useCallback(
    (channel: DemoInvoiceSendChannel) => {
      const { currency, discount, discountType, dueDate, invoiceLines, issueDate } = getValues()
      const { grossTotal } = getInvoiceSummaryTotals({
        currency,
        discountType,
        globalDiscount: discountMode === InvoiceDiscountMode.Global ? discount : undefined,
        invoiceLines,
      })

      setSentState({
        channel,
        companyName: receiver?.name ?? '',
        dueDate,
        grossTotal,
        htmlFiles,
        issueDate,
        sentAt: new Date(),
      })
      openCompletedModal()
    },
    [discountMode, getValues, htmlFiles, openCompletedModal, receiver?.name],
  )

  const handleEmailSent = useCallback(() => showSentInvoice(DemoInvoiceSendChannel.Email), [showSentInvoice])

  const handleEInvoiceSent = useCallback(() => showSentInvoice(DemoInvoiceSendChannel.EInvoice), [showSentInvoice])

  const handleDownload = useCallback(() => showSentInvoice(DemoInvoiceSendChannel.Download), [showSentInvoice])

  const invoiceTitle = sentState
    ? t('external_invoices.view.title.invoice', {
        invoiceNumber: getInvoiceNumberDisplayedValue(DEMO_INVOICE_NUMBER, DEMO_INVOICE_NUMBER_PREFIX),
      })
    : t('demo_invoice.editor_title')

  return (
    <DemoInvoiceLayout onClose={onClose} onReset={onReset}>
      <Form>
        <ModuleLayout
          backButtonTo={backButtonUrl}
          fullHeight
          headerContent={
            !sentState && (
              <Styled.ViewToggleWrapper>
                <DemoInvoiceViewToggle onChange={setView} view={view} />
              </Styled.ViewToggleWrapper>
            )
          }
          sideActions={
            sentState ? (
              <DemoInvoiceDownloadButton htmlFiles={sentState.htmlFiles} />
            ) : (
              <DemoInvoiceFinalizeMenu htmlFiles={htmlFiles} onDownload={handleDownload} />
            )
          }
          title={
            <Text as="span" inline={false} variant="h2">
              {invoiceTitle}
            </Text>
          }
          withBackButton
          withMenuOffset={false}
        >
          <Styled.ContentWrapper>
            {sentState && <DemoInvoiceSentView sentState={sentState} />}
            {!sentState && view === DemoInvoiceView.Editor && (
              <DemoInvoiceForm discountMode={discountMode} onDiscountModeChange={setDiscountMode} />
            )}
            {!sentState && view === DemoInvoiceView.Preview && (
              <DemoInvoicePreview htmlFiles={htmlFiles} isLoading={isLoading} />
            )}
          </Styled.ContentWrapper>
        </ModuleLayout>
      </Form>
      <DemoInvoiceCompletedModal id={ModalId.DemoInvoiceCompletedModal} />
      <DemoInvoiceSendAsEmailModal id={ModalId.DemoInvoiceSendAsEmailModal} onSent={handleEmailSent} />
      <DemoInvoiceSendAsEInvoiceModal
        companyName={receiver?.name ?? ''}
        countryId={receiver?.countryId}
        id={ModalId.DemoInvoiceSendAsEInvoiceModal}
        onSent={handleEInvoiceSent}
      />
    </DemoInvoiceLayout>
  )
}
